import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, CalendarDays, Check, CheckCircle2, Clock3, Crown,
  FileText, Infinity, Lock, MessageCircle, Receipt, RefreshCw, ShieldCheck, Sparkles,
  Wallet, X, XCircle, Zap, PlusCircle, Headphones, CalendarCheck, User, LayoutGrid,
  BarChart3, Folder, CreditCard,
} from "lucide-react";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from '../services/apiHelpers';

/* ─────────────────────────────────────────────
   GLOBAL STYLES — premium blue design system
───────────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,500;0,600;1,500&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@500;600&display=swap');
    *, *::before, *::after { box-sizing: border-box; }

    .sb-root {
      font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
      padding: 24px 24px 60px; max-width: 1280px; width: 100%; margin: 0 auto;
      color: #0d1530; min-height: 0; height: 100%; flex: 1; overflow-y: auto; overflow-x: hidden;
      --font-disp: 'Fraunces', Georgia, serif;
      --font-mono: 'IBM Plex Mono', ui-monospace, monospace;
      --navy-900:#0a1638; --navy-800:#0f1f4d;
      --blue-700:#1749d6; --blue-600:#2f5fe8; --blue-500:#5478ee;
      --blue-100:#eef2fd; --blue-150:#e3eafc; --blue-200:#c8d6f8;
      --ink:#0d1530; --ink-2:#3a4468; --ink-3:#6b7494; --ink-4:#9aa3c0;
      --line:#e2e7f5; --line-2:#cdd7f0; --surface-2:#f6f8fd; --surface-3:#eef1fa;
      --green:#187a4a; --green-bg:#eaf7ef; --green-line:#bfe6cf;
      --amber:#a8620a; --amber-bg:#fdf3e4; --amber-line:#f0d6a3;
      --red:#b3372c; --red-bg:#fbecea; --red-line:#f0c6c0;
      --sh-sm:0 1px 3px rgba(9,16,40,.06);
      --sh-md:0 14px 36px -10px rgba(9,16,40,.16);
      --sh-lg:0 40px 90px -20px rgba(4,8,24,.45);
    }

    /* topbar */
    .sb-topbar { display:flex; justify-content:space-between; align-items:center; gap:16px; margin-bottom:22px; flex-wrap:wrap; }
    .sb-topbar-left { display:flex; align-items:center; gap:14px; min-width:0; }
    .sb-topbar-icon { width:50px; height:50px; border-radius:15px; background:linear-gradient(135deg,var(--blue-600),var(--navy-800)); display:flex; align-items:center; justify-content:center; color:#fff; flex-shrink:0; box-shadow:0 10px 24px rgba(23,73,214,.28); }
    .sb-topbar h2 { font-family:var(--font-disp); font-size:25px; font-weight:600; margin:0; color:var(--ink); letter-spacing:-.01em; overflow-wrap:anywhere; }
    .sb-topbar p { margin:3px 0 0; font-size:13px; color:var(--ink-3); font-weight:500; overflow-wrap:anywhere; }

    .sb-status-badge { display:inline-flex; align-items:center; gap:8px; padding:9px 18px; border-radius:999px; font-size:12.5px; font-weight:700; white-space:nowrap; flex-shrink:0; max-width:100%; min-width:0; }
    .sb-status-badge i { width:7px; height:7px; border-radius:50%; background:currentColor; flex-shrink:0; }
    .sb-status-badge.active { background:var(--green-bg); color:var(--green); border:1px solid var(--green-line); }
    .sb-status-badge.trial { background:var(--blue-100); color:var(--blue-700); border:1px solid var(--blue-200); }
    .sb-status-badge.pending { background:var(--amber-bg); color:var(--amber); border:1px solid var(--amber-line); }
    .sb-status-badge.inactive { background:var(--surface-3); color:var(--ink-3); border:1px solid var(--line-2); }

    .sb-section-eyebrow { display:flex; align-items:center; gap:7px; font-family:var(--font-mono); font-size:10.5px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:var(--blue-700); margin-bottom:6px; }
    .sb-section-eyebrow::before { content:''; width:6px; height:6px; border-radius:50%; background:var(--blue-700); box-shadow:0 0 0 3px var(--blue-100); }
    .sb-root .sb-section-title { font-family:var(--font-disp); font-size:19px; font-weight:600; margin:0; color:var(--ink); }
    .sb-section-divider { height:1px; margin:22px 0; background:linear-gradient(90deg,transparent,var(--line-2) 12%,var(--line-2) 88%,transparent); }

    /* trial banner */
    .sb-trial-banner { background:linear-gradient(135deg,var(--blue-100),#eef2fd); border:1px solid var(--blue-200); border-radius:24px; padding:28px; display:flex; gap:28px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
    .sb-trial-info { flex:1; min-width:240px; }
    .sb-trial-pill { display:inline-flex; align-items:center; gap:6px; background:#fff; border:1px solid var(--blue-200); color:var(--blue-700); border-radius:999px; padding:4px 12px; font-size:11px; font-weight:700; margin-bottom:12px; }
    .sb-trial-title { font-family:var(--font-disp); font-size:22px; font-weight:600; color:var(--navy-800); margin:0 0 6px; }
    .sb-trial-price { font-family:var(--font-mono); font-size:32px; font-weight:800; color:var(--blue-700); line-height:1; }
    .sb-trial-price span { font-size:12px; font-weight:500; color:var(--ink-3); margin-left:4px; }
    .sb-trial-sub { font-size:13px; color:var(--ink-2); margin:8px 0 0; }
    .sb-trial-feats { display:flex; flex-wrap:wrap; gap:8px; margin:16px 0 18px; }
    .sb-trial-feat { background:#fff; border:1px solid var(--blue-200); border-radius:8px; padding:5px 12px; font-size:12px; font-weight:600; color:var(--blue-700); }
    .sb-ring-wrap { flex-shrink:0; }

    /* pending / rejected banners */
    .sb-alert-banner { display:flex; gap:16px; align-items:flex-start; border-radius:24px; padding:24px 26px; margin-bottom:12px; }
    .sb-alert-banner.pending { background:var(--amber-bg); border:1px solid var(--amber-line); }
    .sb-alert-banner.rejected { background:var(--red-bg); border:1px solid var(--red-line); }
    .sb-alert-icon { width:44px; height:44px; border-radius:13px; display:grid; place-items:center; flex-shrink:0; background:#fff; }
    .sb-alert-banner.pending .sb-alert-icon { color:var(--amber); border:1px solid var(--amber-line); }
    .sb-alert-banner.rejected .sb-alert-icon { color:var(--red); border:1px solid var(--red-line); }
    .sb-alert-banner h3 { margin:0 0 5px; font-size:16.5px; font-weight:700; font-family:var(--font-disp); }
    .sb-alert-banner.pending h3 { color:#7a4b08; }
    .sb-alert-banner.rejected h3 { color:#8a2c22; }
    .sb-alert-banner p { margin:0; font-size:13px; line-height:1.6; }
    .sb-alert-banner.pending p { color:#8a5a0a; }
    .sb-alert-banner.rejected p { color:#9a3f34; }

    /* whatsapp addon */
    .sb-addon-card { background:linear-gradient(135deg,var(--blue-100),#f0fdfa); border:1px solid var(--blue-200); border-radius:16px; padding:18px 20px; margin-bottom:12px; }
    .sb-addon-row { display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; }
    .sb-addon-row strong { display:inline-flex; align-items:center; gap:7px; color:var(--navy-800); font-size:16px; }
    .sb-addon-row p { margin:5px 0 0; color:var(--ink-3); font-size:12px; line-height:1.6; max-width:60ch; }
    .sb-addon-toggle { border-radius:8px; padding:9px 14px; cursor:pointer; font-weight:800; font-size:12px; background:#fff; }

    /* usage strip */
    .sb-usage-strip { position:relative; overflow:hidden; border-radius:22px; padding:24px; color:#eef1fc; margin-bottom:18px;
      background:radial-gradient(120% 160% at 100% -20%, rgba(84,120,238,.3), transparent 55%), linear-gradient(150deg,#050b1e 0%, #0a1638 45%, #1749d6 120%);
      box-shadow:0 22px 50px -22px rgba(4,10,28,.5); }
    .sb-usage-heading { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; flex-wrap:wrap; }
    .sb-usage-eyebrow { font-size:11px; letter-spacing:.12em; text-transform:uppercase; opacity:.72; font-weight:700; }
    .sb-usage-planname { font-family:var(--font-disp); font-size:28px; font-weight:600; margin-top:6px; line-height:1.1; }
    .sb-usage-status-pill { border-radius:999px; padding:8px 12px; font-weight:800; font-size:11px; letter-spacing:.08em; text-transform:uppercase; }
    .sb-usage-billing { display:flex; justify-content:space-between; gap:20px; align-items:center; margin-top:18px; flex-wrap:wrap; }
    .sb-usage-price { font-family:var(--font-mono); font-size:38px; font-weight:800; line-height:1; }
    .sb-usage-price span { font-size:13px; opacity:.7; margin-left:6px; }
    .sb-usage-dates { display:flex; flex-wrap:wrap; gap:8px; margin-top:12px; }
    .sb-usage-dates span { background:rgba(255,255,255,.12); border-radius:999px; padding:7px 10px; font-size:11px; font-weight:700; }
    .sb-usage-stats { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px; margin-top:22px; }
    .sb-usage-stat { background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.12); border-radius:14px; padding:12px 14px; }
    .sb-usage-stat .k { font-size:10px; letter-spacing:.08em; text-transform:uppercase; opacity:.7; }
    .sb-usage-stat .v { font-family:var(--font-mono); font-size:26px; font-weight:800; margin-top:6px; }
    .sb-usage-foot { display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-top:20px; font-size:12px; opacity:.85; }
    .sb-usage-note { margin-top:12px; padding:10px 12px; border-radius:10px; background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.16); font-size:12px; line-height:1.5; }
    .sb-usage-note.info { background:rgba(147,197,253,.12); border-color:rgba(147,197,253,.22); }

    /* expired fallback */
    .sb-expired-card { background:linear-gradient(135deg,#fff7ed,#ffedd5); border:1px solid #fdba74; border-radius:22px; padding:20px 22px; margin-bottom:18px; box-shadow:0 16px 30px rgba(249,115,22,.06); }

    /* postpaid bill */
    .sb-bill-card { position:relative; overflow:hidden; background:linear-gradient(135deg,#fffaf5 0%,#fff7ed 26%,#fed7aa 100%); border:1px solid rgba(180,83,9,.28); border-radius:26px; padding:20px 22px 18px; margin-bottom:18px; box-shadow:0 18px 40px rgba(120,53,15,.12), inset 0 1px 0 rgba(255,255,255,.8); }
    .sb-bill-stat { background:rgba(255,255,255,.42); border:1px solid rgba(251,146,60,.2); border-radius:16px; padding:12px 14px; box-shadow:inset 0 1px 0 rgba(255,255,255,.4); }

    /* billing duration toggle */
    .sb-duration-toggle { display:inline-flex; gap:4px; padding:4px; background:var(--surface-3); border-radius:12px; }
    .sb-duration-btn { border:0; border-radius:9px; padding:9px 16px; background:transparent; color:var(--ink-3); font-weight:800; font-size:12.5px; cursor:pointer; transition:.15s; }
    .sb-duration-btn.active { background:#fff; color:var(--blue-700); box-shadow:0 2px 8px rgba(9,16,40,.1); }

    /* plan grid */
    .sb-plans-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:18px; align-items:stretch; justify-content:center; }
    .sb-plans-grid.three-plans { grid-template-columns:repeat(3,minmax(0,1fr)); }

    .sb-plan-card.essential { --pc:#1749d6; --pc-light:#8aa3f1; --pc-soft:#eef2fd; --pc-line:#c8d6f8; --pc-deep:#0a1638; }
    .sb-plan-card.growth    { --pc:#7c3aed; --pc-light:#c3aefb; --pc-soft:#f4f0fe; --pc-line:#ddd0fb; --pc-deep:#3c1a78; }
    .sb-plan-card.scale     { --pc:#0f8a53; --pc-light:#7fd9ab; --pc-soft:#e9faf1; --pc-line:#bdebd1; --pc-deep:#065f33; }
    .sb-plan-card.flexpro   { --pc:#b45309; --pc-light:#f0b85f; --pc-soft:#fdf3e4; --pc-line:#f0d6a3; --pc-deep:#7a3b05; }

    .sb-plan-card { position:relative; background:linear-gradient(170deg,#fff 0%, var(--pc-soft) 230%); border:1.5px solid var(--pc-line); border-radius:24px; padding:24px 22px 22px; display:flex; flex-direction:column; gap:14px; box-shadow:var(--sh-sm); transition:transform .2s, box-shadow .2s, border-color .2s; overflow:hidden; }
    .sb-plan-card::before { content:''; position:absolute; left:0; top:0; right:0; height:4px; background:linear-gradient(90deg,var(--pc),var(--pc-light)); }
    .sb-plan-card:hover { transform:translateY(-4px); box-shadow:var(--sh-md); border-color:var(--pc); }
    .sb-plan-card.sb-current { border-color:var(--pc); box-shadow:0 0 0 2px var(--pc-line), var(--sh-md); }

    .sb-plan-ribbon { position:absolute; top:0; right:0; z-index:3; display:inline-flex; align-items:center; gap:6px; padding:9px 17px 9px 19px; border-radius:0 24px 0 18px; color:#fff; font-size:10px; font-weight:800; letter-spacing:.07em; text-transform:uppercase; white-space:nowrap; }
    .sb-plan-ribbon svg { width:12px; height:12px; flex-shrink:0; }
    .sb-plan-ribbon::after { content:''; position:absolute; inset:-7px; border-radius:inherit; filter:blur(11px); z-index:-1; pointer-events:none; }
    .sb-plan-ribbon.popular { background:linear-gradient(120deg,#5b21b6,#7c3aed,#a78bfa,#7c3aed); background-size:300% 300%; animation:sbRibbonSheen 3.4s linear infinite; }
    .sb-plan-ribbon.popular::after { background:radial-gradient(circle, rgba(124,58,237,.4), transparent 68%); animation:sbRibbonGlow 2.6s ease-in-out infinite; }
    .sb-plan-ribbon.premium { background:linear-gradient(120deg,#8a5207,#d4950f,#f6cd73,#d4950f); background-size:300% 300%; animation:sbRibbonSheen 3.4s linear infinite; }
    .sb-plan-ribbon.premium::after { background:radial-gradient(circle, rgba(212,149,15,.42), transparent 68%); animation:sbRibbonGlow 2.6s ease-in-out infinite; }
    .sb-plan-ribbon.current { background:linear-gradient(135deg,var(--green),#2a9d63); }
    .sb-plan-ribbon.current::after { background:radial-gradient(circle, rgba(16,185,129,.3), transparent 68%); }
    @keyframes sbRibbonSheen { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
    @keyframes sbRibbonGlow { 0%,100%{opacity:.5; transform:scale(1)} 50%{opacity:.85; transform:scale(1.06)} }

    .sb-plan-head { display:flex; align-items:center; gap:11px; margin-top:4px; }
    .sb-plan-icon { width:44px; height:44px; border-radius:13px; display:grid; place-items:center; flex-shrink:0; background:var(--pc-soft); color:var(--pc); box-shadow:inset 0 1px 0 rgba(255,255,255,.6); }
    .sb-plan-name { font-size:15.5px; font-weight:800; color:var(--ink); }
    .sb-plan-desc { font-size:11.5px; color:var(--ink-3); margin-top:2px; }
    .sb-plan-price { font-family:var(--font-mono); font-size:29px; font-weight:800; text-align:center; color:var(--pc); margin-top:2px; }
    .sb-plan-price span { display:block; font-size:10.5px; color:var(--ink-4); font-weight:600; margin-top:3px; }
    .sb-plan-save { font-size:11px; font-weight:800; color:var(--green); text-align:center; background:var(--green-bg); border:1px solid var(--green-line); border-radius:999px; padding:4px 10px; align-self:center; }

    .sb-plan-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:7px; margin-top:2px; }
    .sb-plan-stat { display:flex; flex-direction:column; align-items:center; justify-content:center; min-width:0; padding:8px 2px; text-align:center; }
    .sb-plan-stat svg { width:14px; height:14px; color:var(--pc); }
    .sb-plan-stat .sv { display:block; font-family:var(--font-mono); font-size:12px; font-weight:800; color:var(--pc-deep); margin-top:5px; line-height:1.2; overflow-wrap:normal; }
    .sb-plan-stat .sl { display:block; font-size:8.5px; font-weight:700; color:var(--ink-3); text-transform:uppercase; letter-spacing:.05em; margin-top:2px; }

    .sb-plan-feats { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
    .sb-plan-feats li { display:flex; align-items:flex-start; gap:9px; font-size:12.2px; font-weight:600; color:var(--ink-2); line-height:1.4; }
    .sb-feat-ic { width:21px; height:21px; border-radius:7px; background:var(--pc-soft); color:var(--pc); display:grid; place-items:center; flex-shrink:0; margin-top:1px; }
    .sb-feat-ic svg { width:11px; height:11px; }
    .sb-feat-divider { height:1px; background:linear-gradient(90deg,transparent,var(--pc-line),transparent); margin:2px 0; }
    .sb-plan-feats.secondary { gap:6px; }
    .sb-plan-feats.secondary li { font-size:11px; font-weight:600; color:var(--ink-3); }
    .sb-plan-feats.secondary .sb-feat-ic { width:18px; height:18px; background:var(--surface-3); color:var(--ink-3); }
    .sb-plan-feats.secondary .sb-feat-ic svg { width:9.5px; height:9.5px; }

    .sb-plan-cta { margin-top:auto; height:46px; border-radius:999px; font-size:12.5px; font-weight:800; cursor:pointer; border:1.5px solid var(--pc); background:#fff; color:var(--pc); display:inline-flex; align-items:center; justify-content:center; gap:7px; transition:.18s; font-family:inherit; }
    .sb-plan-cta:hover:not(:disabled) { background:var(--pc); color:#fff; transform:translateY(-1px); }
    .sb-plan-cta:disabled { opacity:.5; cursor:not-allowed; }
    .sb-plan-cta.sb-current { background:linear-gradient(135deg,var(--pc),var(--pc-deep)); color:#fff; border-color:transparent; }

    /* annual usage table */
    .sb-annual-usage { margin:0 0 18px; border:1px solid var(--line); border-radius:20px; background:#fff; box-shadow:0 14px 30px rgba(15,23,42,.06); overflow:hidden; }
    .sb-annual-usage-head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding:20px 22px 16px; background:linear-gradient(135deg,var(--blue-100),#eff6ff); border-bottom:1px solid var(--line); }
    .sb-annual-usage-title { margin:0; color:var(--ink); font-family:var(--font-disp); font-size:18px; font-weight:600; }
    .sb-annual-usage-subtitle { margin:5px 0 0; color:var(--ink-3); font-size:12px; line-height:1.5; }
    .sb-annual-usage-badge { flex-shrink:0; padding:7px 10px; border:1px solid var(--blue-200); border-radius:999px; background:var(--blue-100); color:var(--blue-700); font-size:10px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; }
    .sb-annual-usage-scroll { overflow-x:auto; }
    .sb-annual-usage-table { width:100%; min-width:760px; border-collapse:collapse; font-variant-numeric:tabular-nums; }
    .sb-annual-usage-table th { padding:12px 16px; background:var(--surface-2); border-bottom:1px solid var(--line); color:var(--ink-3); font-size:10px; font-weight:800; letter-spacing:.06em; text-align:right; text-transform:uppercase; white-space:nowrap; }
    .sb-annual-usage-table th:first-child, .sb-annual-usage-table td:first-child { text-align:left; }
    .sb-annual-usage-table td { padding:14px 16px; border-bottom:1px solid var(--line); color:var(--ink-2); font-size:12px; font-weight:700; text-align:right; white-space:nowrap; }
    .sb-annual-usage-table tbody tr:last-child td { border-bottom:0; }
    .sb-annual-usage-table tbody tr.current { background:var(--blue-100); }
    .sb-annual-cycle-label { color:var(--ink); font-weight:800; }
    .sb-annual-cycle-current { display:inline-block; margin-left:8px; padding:3px 7px; border-radius:999px; background:var(--blue-200); color:var(--blue-700); font-size:9px; font-weight:800; letter-spacing:.05em; text-transform:uppercase; }
    .sb-annual-usage-table tfoot td { padding:15px 16px; border-top:2px solid var(--line-2); background:var(--surface-2); color:var(--ink); font-weight:900; }
    .sb-annual-usage-note { margin:0; padding:12px 22px 16px; color:var(--ink-3); font-size:11px; line-height:1.5; }
    .sb-empty { text-align:center; padding:56px 20px; color:var(--ink-4); }
    .sb-empty strong { display:block; font-size:15px; color:var(--ink-3); margin-bottom:4px; }
    .sb-empty p { margin:0; font-size:13px; }

    /* invoices */
    .sb-billing-head { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-top:36px; margin-bottom:16px; }
    .sb-history-btn { display:inline-flex; align-items:center; gap:8px; padding:10px 16px; border-radius:999px; border:1px solid var(--blue-200); background:var(--blue-100); color:var(--blue-700); font-size:12.5px; font-weight:700; cursor:pointer; transition:.18s; white-space:nowrap; font-family:inherit; }
    .sb-history-btn:hover { background:var(--blue-200); transform:translateY(-1px); }
    .sb-inv-wrap { overflow-x:auto; border:1px solid var(--line); border-radius:20px; }
    .sb-inv-table { width:100%; min-width:520px; border-collapse:collapse; font-size:13.5px; }
    .sb-inv-table thead tr { background:linear-gradient(135deg,var(--navy-800),var(--blue-700)); }
    .sb-inv-table thead th { padding:13px 18px; text-align:left; font-size:10.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:rgba(255,255,255,.88); white-space:nowrap; }
    .sb-inv-table tbody tr { border-bottom:1px solid var(--line); transition:background .15s; }
    .sb-inv-table tbody tr:last-child { border-bottom:none; }
    .sb-inv-table tbody tr:hover { background:var(--surface-2); }
    .sb-inv-table td { padding:14px 18px; color:var(--ink-2); vertical-align:middle; }
    .sb-inv-icon { width:32px; height:32px; border-radius:9px; background:var(--blue-100); color:var(--blue-700); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .sb-inv-num { font-weight:700; color:var(--ink); font-size:13.5px; }
    .sb-inv-month { font-size:11.5px; color:var(--ink-4); margin-top:1px; }
    .sb-inv-amount { font-weight:800; font-size:15px; color:var(--ink); font-variant-numeric:tabular-nums; font-family:var(--font-mono); }
    .sb-inv-paid { display:inline-flex; align-items:center; gap:4px; background:var(--green-bg); border:1px solid var(--green-line); color:var(--green); border-radius:999px; padding:3px 10px; font-size:11px; font-weight:700; }

    /* loading */
    .sb-loading { display:flex; height:60vh; align-items:center; justify-content:center; flex-direction:column; gap:14px; }
    .sb-spinner { width:36px; height:36px; border-radius:50%; border:3px solid var(--line-2); border-top-color:var(--blue-700); animation:sbSpin .7s linear infinite; }
    @keyframes sbSpin { to{transform:rotate(360deg)} }

    /* toast */
    .sb-toast { position:fixed; top:20px; right:20px; z-index:8000; display:flex; align-items:center; gap:10px; padding:12px 18px; border-radius:14px; font-weight:600; font-size:13px; box-shadow:var(--sh-lg); backdrop-filter:blur(12px); min-width:280px; }
    .sb-toast.error { background:rgba(255,242,242,.97); border:1px solid var(--red-line); color:var(--red); }
    .sb-toast.success { background:rgba(234,247,239,.97); border:1px solid var(--green-line); color:var(--green); }

    /* modal */
    .sb-backdrop { position:fixed; inset:0; background:rgba(9,16,40,.6); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; padding:16px; z-index:5000; }
    .sb-modal { background:#fff; border-radius:22px; box-shadow:var(--sh-lg); max-width:460px; width:100%; max-height:92vh; overflow-y:auto; }
    .sb-modal-head { padding:22px 28px 18px; background:linear-gradient(135deg,var(--navy-800),var(--blue-700)); border-radius:22px 22px 0 0; display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
    .sb-modal-title { margin:0; font-family:var(--font-disp); font-size:18px; font-weight:600; color:#fff; }
    .sb-modal-subtitle { margin:4px 0 0; font-size:12px; color:#c2cdf5; font-weight:500; }
    .sb-modal-close { width:32px; height:32px; border-radius:50%; border:none; background:rgba(255,255,255,.15); color:#fff; cursor:pointer; font-size:18px; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:background .15s; }
    .sb-modal-close:hover { background:rgba(255,255,255,.25); }
    .sb-modal-body { padding:24px 28px; }
    .sb-modal-footer { padding:16px 28px; border-top:1px solid var(--line); display:flex; flex-direction:column; gap:8px; background:var(--surface-2); border-radius:0 0 22px 22px; }
    .sb-order-row { display:flex; justify-content:space-between; align-items:center; padding:11px 14px; border-radius:10px; background:var(--surface-2); border:1px solid var(--line); margin-bottom:10px; }
    .sb-order-row-label { display:flex; align-items:center; gap:9px; font-size:13px; font-weight:600; color:var(--ink-2); }
    .sb-order-row-price { font-size:14px; font-weight:800; color:var(--ink); font-variant-numeric:tabular-nums; font-family:var(--font-mono); }
    .sb-divider { height:1px; background:linear-gradient(90deg,transparent,var(--line-2),transparent); margin:14px 0; }
    .sb-total-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:4px; }
    .sb-total-label { font-size:13px; font-weight:600; color:var(--ink-3); }
    .sb-total-val { font-size:26px; font-weight:800; color:var(--ink); font-variant-numeric:tabular-nums; font-family:var(--font-mono); }
    .sb-primary-btn { width:100%; padding:13px 20px; border-radius:999px; border:none; background:linear-gradient(135deg,var(--blue-600),var(--blue-700)); color:#fff; font-weight:700; font-size:14px; font-family:inherit; cursor:pointer; box-shadow:0 10px 24px rgba(23,73,214,.3); transition:all .18s ease; display:flex; align-items:center; justify-content:center; gap:8px; }
    .sb-primary-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 14px 30px rgba(23,73,214,.4); }
    .sb-primary-btn:disabled { background:var(--surface-3); color:var(--ink-4); cursor:not-allowed; box-shadow:none; }
    .sb-ghost-btn { width:100%; padding:12px 20px; border-radius:999px; border:1.5px solid var(--line-2); background:transparent; color:var(--ink-3); font-weight:600; font-size:13.5px; font-family:inherit; cursor:pointer; transition:all .15s ease; }
    .sb-ghost-btn:hover { background:var(--surface-2); }
    .sb-btn-spinner { width:16px; height:16px; border-radius:50%; border:2.5px solid rgba(255,255,255,.3); border-top-color:#fff; animation:sbSpin .7s linear infinite; display:inline-block; }

    /* success modal */
    .cf-piece { position:absolute; border-radius:2px; animation:cfFall 1.5s ease-out forwards; }
    @keyframes cfFall { 0%{transform:translateY(-40px) rotate(0) scale(1); opacity:1} 100%{transform:translateY(220px) rotate(600deg) scale(.4); opacity:0} }
    .p-ring { position:absolute; inset:-8px; border-radius:50%; border:3px solid var(--blue-700); animation:sbPRing 1.5s ease-out infinite; }
    @keyframes sbPRing { 0%{transform:scale(.85); opacity:.9} 70%{transform:scale(1.18); opacity:0} 100%{transform:scale(.85); opacity:0} }
    .chk-path { stroke-dasharray:60; stroke-dashoffset:60; animation:sbDrawCheck .5s .3s cubic-bezier(.4,0,.2,1) forwards; }
    @keyframes sbDrawCheck { from{stroke-dashoffset:60} to{stroke-dashoffset:0} }

    /* responsive */
    @media (max-width:1100px) {
      .sb-plans-grid { grid-template-columns:repeat(2,1fr); }
      .sb-plans-grid.three-plans { grid-template-columns:repeat(2,minmax(0,1fr)); }
      .sb-plans-grid.three-plans > :last-child { grid-column:1/-1; width:calc(50% - 9px); justify-self:center; }
      .sb-usage-stats { grid-template-columns:repeat(2,1fr); }
    }
    @media (max-width:900px) {
      .sb-topbar { align-items:stretch; }
      .sb-status-badge { width:100%; justify-content:center; }
    }
    @media (max-width:768px) {
      .sb-root { padding:16px 14px 52px; }
      .sb-topbar { flex-direction:column; align-items:stretch; gap:12px; }
      .sb-trial-banner, .sb-usage-billing { flex-direction:column; align-items:stretch; }
      .sb-ring-wrap, .sb-usage-ring-wrap { align-self:center; }
      .sb-plans-grid, .sb-plans-grid.three-plans { grid-template-columns:1fr; }
      .sb-plans-grid.three-plans > :last-child { grid-column:auto; width:100%; justify-self:stretch; }
      .sb-usage-stats { grid-template-columns:repeat(2,1fr); }
      .sb-inv-table { min-width:620px; }
      .sb-annual-usage-table { min-width:720px; }
    }
    @media (prefers-reduced-motion: reduce) {
      .sb-spinner, .sb-btn-spinner, .p-ring, .chk-path, .cf-piece, .sb-plan-ribbon, .sb-plan-ribbon::after { animation:none; }
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   Confetti
───────────────────────────────────────────── */
const Confetti = () => {
  const cols = ["#2563eb","#3b82f6","#60a5fa","#1749d6","#93c5fd","#dbeafe","#1e40af"];
  return (
    <div style={{ position:"absolute", top:0, left:0, width:"100%", height:260, overflow:"hidden", pointerEvents:"none" }}>
      {Array.from({ length: 26 }, (_, i) => (
        <div key={i} className="cf-piece" style={{
          left:`${(i/26)*100}%`, backgroundColor:cols[i%cols.length], animationDelay:`${(i%6)*.12}s`,
          width:`${6+(i%4)*2}px`, height:`${6+(i%4)*2}px`,
          borderRadius:i%3===0?"50%":i%3===1?"2px":"50% 0 0 50%",
        }} />
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Success Modal
───────────────────────────────────────────── */
const SuccessModal = ({ onDone, isTrialActivation, isPlanChange }) => {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position:"fixed", inset:0, background:"rgba(9,16,40,.6)", backdropFilter:"blur(12px)",
               display:"flex", alignItems:"center", justifyContent:"center", zIndex:9000, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <motion.div
        initial={{ scale:.65, opacity:0, y:30 }} animate={{ scale:1, opacity:1, y:0 }} exit={{ scale:.8, opacity:0, y:-20 }}
        transition={{ type:"spring", stiffness:400, damping:28 }}
        style={{ background:"#fff", borderRadius:24, padding:"56px 52px 48px", textAlign:"center",
                 boxShadow:"0 32px 72px rgba(9,16,40,.22)", width:400, maxWidth:"92vw",
                 position:"relative", overflow:"hidden", border:"1px solid #e2e7f5" }}>
        <Confetti />
        <div style={{ position:"relative", display:"inline-flex", margin:"0 auto 24px" }}>
          <div className="p-ring" />
          <motion.div initial={{ scale:0 }} animate={{ scale:1 }} transition={{ delay:.1, type:"spring", stiffness:320, damping:20 }}
            style={{ width:76, height:76, borderRadius:"50%", background:"linear-gradient(135deg,#1749d6 0%,#5478ee 100%)",
                     display:"flex", alignItems:"center", justifyContent:"center",
                     boxShadow:"0 10px 28px rgba(23,73,214,.38)", position:"relative", zIndex:1 }}>
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
              <path className="chk-path" d="M7 17 L14 25 L27 9" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.div>
        </div>
        <motion.h2 initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:.32 }}
          style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:22, fontWeight:600, color:"#0d1530", marginBottom:8 }}>
          {isTrialActivation ? "Payment scheduled successfully" : isPlanChange ? "Plan change confirmed" : "Subscription activated!"}
        </motion.h2>
        <motion.p initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:.42 }}
          style={{ fontSize:14, color:"#6b7494", lineHeight:1.65, marginBottom:24 }}>
          {isTrialActivation
            ? "Your payment is confirmed. The paid subscription is scheduled to start after your trial ends, so your current trial access continues without interruption."
            : isPlanChange
              ? "Your payment is confirmed and your plan change has been scheduled according to your current billing period."
              : "Your subscription is active and ready to use. Thank you for choosing CliniQueue."}
        </motion.p>
        <div style={{ width:"100%", height:3, background:"#eef1fa", borderRadius:99, overflow:"hidden" }}>
          <motion.div initial={{ width:"100%" }} animate={{ width:"0%" }} transition={{ duration:3, ease:"linear" }}
            style={{ height:"100%", background:"linear-gradient(90deg,#1749d6,#5478ee)", borderRadius:99 }} />
        </div>
        <p style={{ fontSize:10, color:"#9aa3c0", marginTop:7, fontWeight:700, letterSpacing:.4 }}>AUTO-CLOSING IN 3s</p>
      </motion.div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   Feature icon mapping + secondary-feature heuristic
───────────────────────────────────────────── */
function FeatureIcon({ text }) {
  const t = text.toLowerCase();
  let Icon = CheckCircle2;
  if (t.includes("additional")) Icon = PlusCircle;
  else if (t.includes("support")) Icon = Headphones;
  else if (t.includes("visit")) Icon = CalendarCheck;
  else if (t.includes("doctor account")) Icon = User;
  else if (t.includes("core clinic management")) Icon = LayoutGrid;
  else if (t.includes("analytics")) Icon = BarChart3;
  else if (t.includes("rx") || t.includes("prescription")) Icon = FileText;
  else if (t.includes("records")) Icon = Folder;
  else if (t.includes("notify") || t.includes("connect") || t.includes("whatsapp")) Icon = MessageCircle;
  else if (t.includes("payflow") || t.includes("upi")) Icon = CreditCard;
  else if (t.includes("pay-as-you-go") || t.includes("per patient visit") || t.includes("minimum")) Icon = Wallet;
  return <span className="sb-feat-ic"><Icon size={11} strokeWidth={2.4} /></span>;
}
function isSecondaryFeature(text) {
  const t = text.toLowerCase();
  return t.includes("additional") || t.includes("support");
}

/* ─────────────────────────────────────────────
   Annual usage table
───────────────────────────────────────────── */
const AnnualUsageTable = ({ data }) => {
  if (data?.mode !== "SUBSCRIBED" || Number(data?.durationMonths) !== 12 || !data?.annualUsageEnabled) {
    return null;
  }

  const cycles = Array.isArray(data.annualUsage) ? data.annualUsage : [];
  const totals = data.annualUsageTotals || {};
  const number = (value) => Number(value ?? 0).toLocaleString("en-IN");

  return (
    <motion.section className="sb-annual-usage" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .16 }} aria-labelledby="annual-usage-title">
      <div className="sb-annual-usage-head">
        <div>
          <div className="sb-section-eyebrow" style={{ marginTop: 0 }}>Annual usage</div>
          <h3 id="annual-usage-title" className="sb-annual-usage-title">12-month visit balance</h3>
          <p className="sb-annual-usage-subtitle">Unused visits continue into the next cycle and reset only when the annual subscription renews or ends.</p>
        </div>
        <span className="sb-annual-usage-badge">Annual visit pool</span>
      </div>

      {cycles.length === 0 ? (
        <div className="sb-empty">
          <strong>Annual usage is preparing</strong>
          <p>Your cycle details will appear after the subscription dates are available.</p>
        </div>
      ) : (
        <div className="sb-annual-usage-scroll">
          <table className="sb-annual-usage-table">
            <thead>
              <tr><th scope="col">Billing cycle</th><th scope="col">Annual pool</th><th scope="col">Used</th><th scope="col">Remaining</th></tr>
            </thead>
            <tbody>
              {cycles.map((cycle) => (
                <tr key={cycle.cycleNumber} className={cycle.current ? "current" : ""}>
                  <td><span className="sb-annual-cycle-label">{cycle.cycleLabel}</span>{cycle.current && <span className="sb-annual-cycle-current">Current</span>}</td>
                  <td>{number(cycle.annualAllowance ?? totals.annualAllowance)}</td>
                  <td>{number(cycle.usedAppointments)}</td>
                  <td style={{ color: cycle.current ? "#187a4a" : "var(--ink-2)" }}>{number(cycle.remainingAppointments)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td>Annual total</td><td>{number(totals.annualAllowance)}</td><td>{number(totals.usedAppointments)}</td><td>{number(totals.remainingAppointments)}</td></tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="sb-annual-usage-note">Each date range is inclusive and non-overlapping. Example: Cycle 1 ends on 15 Oct, and Cycle 2 begins on 16 Oct.</p>
    </motion.section>
  );
};

/* ═══════════════════════════════ MAIN COMPONENT ═══════════════════════════════ */
const SubscriptionPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentPreview, setPaymentPreview] = useState(null);
  const [upgradeConfirm, setUpgradeConfirm] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [toast, setToast] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successContext, setSuccessContext] = useState({ isTrialActivation: false, isPlanChange: false });
  const [isTrialMode, setIsTrialMode] = useState(false);
  const [whatsappAddonEnabled, setWhatsappAddonEnabled] = useState(false);
  const [whatsappToggleLoading, setWhatsappToggleLoading] = useState(false);
  const [billingDuration, setBillingDuration] = useState(1);
  const [planIds, setPlanIds] = useState({});
  const [backendPlans, setBackendPlans] = useState({});
  const toastRef = useRef(null);

  const { auth } = useAuth();
  const clinicId = auth?.clinic?.id || null;
  const navigate = useNavigate();
  const { refreshSubscription } = useSubscription();

  const openInvoicesPage = () => navigate("/admin-dashboard/invoices");

  const extractErrorMsg = (err, fallback) => {
    const d = err.response?.data;
    if (typeof d === "string") return d;
    return d?.message || fallback;
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    window.clearTimeout(toastRef.current);
    toastRef.current = window.setTimeout(() => setToast(null), 4200);
  };

  /* ── data fetching (unchanged from source) ── */
  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(buildApiUrl("plans/all"));
      const idsByCardId = {};
      const plansByCardId = {};
      res.data?.forEach(plan => {
        const normalizedName = String(plan?.planName || "").trim().toLowerCase();
        const cardId = { essential: 1, growth: 2, scale: 3, "flex pro": 4 }[normalizedName];
        if (cardId && plan?.id != null) {
          idsByCardId[cardId] = plan.id;
          plansByCardId[cardId] = plan;
        }
      });
      setPlanIds(idsByCardId);
      setBackendPlans(plansByCardId);
    } catch (err) {
      showToast(extractErrorMsg(err, "Failed to load subscription plans"), "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const fetchDashboard = useCallback(async () => {
    if (!clinicId) { setLoading(false); return; }
    try {
      const dashboardData = await refreshSubscription();
      if (!dashboardData) {
        showToast("Failed to load subscription", "error");
        return;
      }
      setData(dashboardData);
      setWhatsappAddonEnabled(dashboardData?.whatsappAddonEnabled === true);

      if (dashboardData?.mode === "SUBSCRIBED") {
        setIsTrialMode(false);
      } else if (dashboardData?.mode === "TRIAL") {
        setIsTrialMode(true);
      } else if (dashboardData?.mode === "PENDING_APPROVAL") {
        setIsTrialMode(false);
      } else {
        localStorage.removeItem("trialEndDate");
        setIsTrialMode(false);
      }
    } catch (err) {
      showToast(extractErrorMsg(err, "Failed to load subscription"), "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, refreshSubscription]);

  const toggleWhatsAppCommunication = async () => {
    const enabled = !whatsappAddonEnabled;
    setWhatsappToggleLoading(true);
    try {
      const response = await axios.post(buildApiUrl("clinic/whatsapp/communication/toggle"), null, {
        params: { clinicId, enabled },
        headers: { "X-Clinic-Id": clinicId },
      });
      setWhatsappAddonEnabled(response.data?.enabled === true);
      setData(current => current ? { ...current, whatsappAddonEnabled: response.data?.enabled === true } : current);
      showToast(enabled ? "WhatsApp Patient Communication enabled." : "WhatsApp Patient Communication paused.");
    } catch (err) {
      showToast(extractErrorMsg(err, "Unable to update WhatsApp communication"), "error");
    } finally {
      setWhatsappToggleLoading(false);
    }
  };

  const fetchRecentInvoices = useCallback(async () => {
    if (!clinicId) { setLoading(false); return; }
    try {
      const res = await axios.get(buildApiUrl(`subscription/invoices/${clinicId}`), { headers: { "X-Clinic-Id": clinicId } });
      setRecentInvoices(res.data?.slice(0, 3) ?? []);
    } catch {
      console.error("Invoice fetch failed");
    }
  }, [clinicId]);

  useEffect(() => { fetchPlans(); fetchDashboard(); fetchRecentInvoices(); }, [fetchPlans, fetchDashboard, fetchRecentInvoices]);

  /* ── payment flow (unchanged from source) ── */
  const handlePlanClick = (planId) => {
    if (planId == null) {
      showToast("This plan is currently unavailable. Please contact support.", "error");
      return;
    }
    const selectedDuration = planId === planIds[4] ? 1 : billingDuration;
    if (data?.planId && data.planId !== planId) {
      setUpgradeConfirm({ planId, durationMonths: selectedDuration });
      return;
    }
    createOrder(planId, selectedDuration);
  };

  const createOrder = async (planId, durationMonths = 1) => {
    try {
      setProcessing(true);
      const res = await axios.post(buildApiUrl(`payment/create-order`), null, { params: { clinicId, planId, durationMonths }, headers: { "X-Clinic-Id": clinicId } });
      setPaymentPreview({ ...res.data, planId, durationMonths });
    } catch (err) {
      showToast(extractErrorMsg(err, "Payment initialization failed"), "error");
    } finally {
      setProcessing(false);
    }
  };

  const proceedToPayment = async () => {
    if (typeof window === "undefined" || typeof window.Razorpay !== "function") {
      showToast("Payment checkout is unavailable right now. Please refresh and try again.", "error");
      return;
    }
    const { orderId, amount, key, planId, durationMonths } = paymentPreview;
    const options = {
      key, amount, currency: "INR",
      name: "CliniQueue", description: "Plan Activation", order_id: orderId,
      handler: async (response) => {
        try {
          setProcessing(true);
          await axios.post(buildApiUrl(`payment/verify`), null, {
            params: {
              clinicId, planId, durationMonths,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            },
            headers: { "X-Clinic-Id": clinicId },
          });
          await fetchDashboard();
          await fetchRecentInvoices();
          setPaymentPreview(null);
          setSuccessContext({
            isTrialActivation: data?.mode === "TRIAL",
            isPlanChange: Boolean(data?.planId && data.planId !== planId),
          });
          setShowSuccessModal(true);
        } catch (err) {
          showToast(extractErrorMsg(err, "Payment verification failed"), "error");
        } finally {
          setProcessing(false);
        }
      },
      theme: { color: "#1749d6" },
    };
    new window.Razorpay(options).open();
  };

  /* ── derived values (unchanged from source) ── */
  const annualUsageEnabled = Boolean(data?.annualUsageEnabled && Number(data?.durationMonths) === 12);
  const isFlexProPostpaid = Boolean(data?.isPostpaid && typeof data?.planName === "string" && data.planName.toLowerCase().includes("flex pro"));

  const usagePercent = isFlexProPostpaid
    ? (Number(data?.usedAppointments ?? 0) > 0 ? 100 : 0)
    : data?.usedAppointments && data?.totalAllowed
      ? Math.min(100, Math.round((data.usedAppointments / data.totalAllowed) * 100))
      : 0;

  const remaining = isFlexProPostpaid
    ? 0
    : data?.remainingAppointments ?? (data?.totalAllowed != null && data?.usedAppointments != null ? Math.max(data.totalAllowed - data.usedAppointments, 0) : 0);

  const parseDashboardDate = (value) => {
    if (!value) return null;
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(value);
  };

  const billDueDate = data?.invoiceDueDate || data?.endDate;
  const showMonthlyBillCard = Boolean(
    data?.billVisible !== false &&
    data?.isPostpaid &&
    Number(data?.outstandingAmount ?? 0) > 0 &&
    data?.endDate &&
    new Date() >= new Date(parseDashboardDate(data.endDate)) &&
    (!billDueDate || new Date() <= new Date(parseDashboardDate(billDueDate)))
  );

  const trialDaysLeft = () => {
    if (!data?.trialEndDate) return null;
    const d = Math.ceil((new Date(data.trialEndDate) - new Date()) / 86400000);
    return d > 0 ? d : 0;
  };

  const formatDashboardDate = (value) => {
    const date = parseDashboardDate(value);
    if (!date || Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-IN", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" });
  };

  const formattedTrialEndDate = () => formatDashboardDate(data?.trialEndDate);
  const formattedPendingStartDate = () => formatDashboardDate(data?.pendingStartDate);
  const formattedPendingEndDate = () => formatDashboardDate(data?.pendingEndDate);
  const daysLeft = trialDaysLeft();

  /* ── plan definitions (same copy as source, + quick-glance stats + theme key) ── */
  const planDetails = {
    1: {
      key: "essential", name: "Essential", price: 799, quarterlyPrice: 2199, yearlyPrice: 7999, icon: ShieldCheck,
      desc: "Best for solo clinics",
      stats: { visits: "300", visitsYearly: "4,000", doctors: "1", support: "Standard" },
      features: [
        "300 Patient Visits / month", "10% Complimentary Visit Allowance", "1 Doctor Account",
        "Core Clinic Management", "Clinic Analytics & Reporting", "CliniQueue Rx | Digital prescriptions",
        "CliniQueue Records | Patient records & reports", "CliniQueue Notify | Essential WhatsApp notifications",
        "Additional Visit | ₹3 / visit after allowance", "Additional Doctor | ₹299 one-time", "Standard Support",
      ],
      yearlyFeatures: [
        "Up to 4,000 Patient Visits / year", "Annual Visit Pool | No monthly limit", "Use visits anytime during the 12-month subscription",
        "1 Doctor Account", "Core Clinic Management", "Clinic Analytics & Reporting", "CliniQueue Rx | Digital prescriptions",
        "CliniQueue Records | Patient records & reports", "CliniQueue Notify | Essential WhatsApp notifications",
        "Additional Visit | ₹3 / visit after annual pool", "Additional Doctor | ₹299 one-time", "Standard Support",
      ],
    },
    2: {
      key: "growth", name: "Growth", price: 1399, quarterlyPrice: 3799, yearlyPrice: 13999, icon: Zap,
      desc: "Best for growing clinics", popular: true,
      stats: { visits: "750", visitsYearly: "10,000", doctors: "3", support: "Priority" },
      features: [
        "750 Patient Visits / month", "10% Complimentary Visit Allowance", "3 Doctor Accounts",
        "Core Clinic Management", "Clinic Analytics & Reporting", "CliniQueue Rx | Digital prescriptions",
        "CliniQueue Records | Patient records & reports", "CliniQueue Notify | Essential WhatsApp notifications",
        "Additional Visit | ₹3 / visit after allowance", "Additional Doctor | ₹299 one-time", "Priority Support | Email & Phone",
      ],
      yearlyFeatures: [
        "Up to 10,000 Patient Visits / year", "Annual Visit Pool | No monthly limit", "Use visits anytime during the 12-month subscription",
        "3 Doctor Accounts", "Core Clinic Management", "Clinic Analytics & Reporting", "CliniQueue Rx | Digital prescriptions",
        "CliniQueue Records | Patient records & reports", "CliniQueue Notify | Essential WhatsApp notifications",
        "Additional Visit | ₹3 / visit after annual pool", "Additional Doctor | ₹299 one-time", "Priority Support | Email & Phone",
      ],
    },
    3: {
      key: "scale", name: "Scale", price: 2199, quarterlyPrice: 5999, yearlyPrice: 21999, icon: Crown,
      desc: "Best for multi-doctor practices",
      stats: { visits: "1,500", visitsYearly: "20,000", doctors: "5", support: "Dedicated" },
      features: [
        "1,500 Patient Visits / month", "10% Complimentary Visit Allowance", "5 Doctor Accounts",
        "Core Clinic Management", "Clinic Analytics & Reporting", "CliniQueue Rx | Digital prescriptions",
        "CliniQueue Records | Patient records & reports", "CliniQueue Notify | Essential WhatsApp notifications",
        "Additional Visit | ₹3 / visit after allowance", "Additional Doctor | ₹299 one-time", "Priority Support | Dedicated Phone & Email",
      ],
      yearlyFeatures: [
        "Up to 20,000 Patient Visits / year", "Annual Visit Pool | No monthly limit", "Use visits anytime during the 12-month subscription",
        "5 Doctor Accounts", "Core Clinic Management", "Clinic Analytics & Reporting", "CliniQueue Rx | Digital prescriptions",
        "CliniQueue Records | Patient records & reports", "CliniQueue Notify | Essential WhatsApp notifications",
        "Additional Visit | ₹3 / visit after annual pool", "Additional Doctor | ₹299 one-time", "Priority Support | Dedicated Phone & Email",
      ],
    },
    4: {
      key: "flexpro", name: "Flex Pro", price: 999, quarterlyPrice: null, yearlyPrice: null, icon: Infinity,
      desc: "Unlimited doctors, flexible billing", flexpro: true,
      stats: { doctors: "Unlimited", rate: "₹3/visit", billing: "Pay as you go" },
      features: [
        "Unlimited Doctor Accounts", "Flexible Pay-as-you-go Billing", "₹3 per Patient Visit", "₹999 Minimum Monthly Billing",
        "No Prepaid Visit Allowance", "Core Clinic Management", "Clinic Analytics & Reporting", "CliniQueue Rx | Digital prescriptions",
        "CliniQueue Records | Patient records & reports", "CliniQueue Connect | Complete WhatsApp communication",
        "CliniQueue PayFlow | UPI payments & digital receipts", "Priority Support",
      ],
    },
  };

  const getPlanPrice = (id, duration = 1) => {
    const fallback = planDetails[id];
    const backend = backendPlans[id] || {};
    const backendPrice = duration === 3
      ? (backend.quarterlyPrice ?? backend.threeMonthPrice)
      : duration === 12
        ? (backend.yearlyPrice ?? backend.annualPrice)
        : (backend.monthlyPrice ?? backend.price);
    if (backendPrice != null && Number.isFinite(Number(backendPrice))) return Number(backendPrice);
    return duration === 3 ? (fallback.quarterlyPrice ?? fallback.price) : duration === 12 ? (fallback.yearlyPrice ?? fallback.price) : fallback.price;
  };

  const getBillingSavings = (id) => {
    if (billingDuration === 1) return null;
    const selectedPrice = getPlanPrice(id, billingDuration);
    if (!selectedPrice) return null;
    const regularPrice = getPlanPrice(id, 1) * billingDuration;
    const savingsAmt = regularPrice - selectedPrice;
    return { amount: savingsAmt, percentage: (savingsAmt / regularPrice) * 100 };
  };

  const planStats = (plan) => {
    const s = plan.stats || {};
    let tiles;
    if (plan.flexpro) {
      tiles = [
        { Icon: User, val: s.doctors, lbl: "Doctors" },
        { Icon: Wallet, val: s.rate, lbl: "Per visit" },
        { Icon: CalendarCheck, val: s.billing, lbl: "Billing" },
      ];
    } else {
      const visitsVal = billingDuration === 12 ? s.visitsYearly : s.visits;
      tiles = [
        { Icon: CalendarCheck, val: visitsVal, lbl: billingDuration === 12 ? "Visits / yr" : "Visits / mo" },
        { Icon: User, val: s.doctors, lbl: s.doctors === "1" ? "Doctor" : "Doctors" },
        { Icon: Headphones, val: s.support, lbl: "Support" },
      ];
    }
    return (
      <div className="sb-plan-stats">
        {tiles.map((t, i) => (
          <div className="sb-plan-stat" key={i}>
            <t.Icon size={14} strokeWidth={2.2} />
            <span className="sv">{t.val}</span>
            <span className="sl">{t.lbl}</span>
          </div>
        ))}
      </div>
    );
  };

  /* ── LOADING ── */
  if (loading) return (
    <>
      <GlobalStyles />
      <div className="sb-loading">
        <div className="sb-spinner" />
        <span style={{ fontSize: 14, color: "#6b7494", fontWeight: 500 }}>Loading billing dashboard…</span>
      </div>
    </>
  );

  return (
    <>
      <GlobalStyles />

      <AnimatePresence>
        {showSuccessModal && (
          <SuccessModal {...successContext} onDone={() => { setShowSuccessModal(false); setTimeout(() => window.location.reload(), 200); }} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div className={`sb-toast ${toast.type}`} initial={{ opacity: 0, y: -18, scale: .96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -14, scale: .96 }}>
            {toast.type === "error" ? <XCircle size={16} /> : <Check size={16} />}
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="sb-root">

        {/* Header */}
        <div className="sb-topbar">
          <div className="sb-topbar-left">
            <div className="sb-topbar-icon"><Wallet size={26} aria-hidden="true" /></div>
            <div>
              <h2>Billing &amp; Plans</h2>
              <p>Manage your clinic subscription and payment history</p>
            </div>
          </div>

          {data?.mode === "SUBSCRIBED" && (
            <div className="sb-status-badge active"><i /><BadgeCheck size={15} aria-hidden="true" /> Active — {data.planName}{data?.isPostpaid === true && <span style={{ fontSize: 11, opacity: .85, marginLeft: 4 }}>· Payment due: 3 days</span>}</div>
          )}
          {data?.mode === "TRIAL" && (
            <div className="sb-status-badge trial"><i /><Clock3 size={15} aria-hidden="true" /> Free trial · {daysLeft} days left</div>
          )}
          {data?.mode === "PENDING_APPROVAL" && (
            <div className="sb-status-badge pending"><i /><Clock3 size={15} aria-hidden="true" /> Pending approval</div>
          )}
          {data?.mode !== "SUBSCRIBED" && data?.mode !== "TRIAL" && data?.mode !== "PENDING_APPROVAL" && (
            <div className="sb-status-badge inactive"><i /><XCircle size={15} aria-hidden="true" /> No active plan</div>
          )}
        </div>

        {/* Trial banner */}
        {data?.mode === "TRIAL" && (
          <motion.div className="sb-trial-banner" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .1 }}>
            <div className="sb-trial-info">
              <span className="sb-trial-pill">🎉 Free trial active</span>
              <div className="sb-trial-title">14-day free trial</div>
              <div className="sb-trial-price">₹0<span>/month</span></div>
              <p className="sb-trial-sub">All features unlocked until {formattedTrialEndDate()}</p>
              {data?.pendingStartDate && data?.pendingEndDate && (
                <p className="sb-trial-sub" style={{ marginTop: 8, fontWeight: 600 }}>
                  Your paid subscription is scheduled to start on {formattedPendingStartDate()} and end on {formattedPendingEndDate()}.
                </p>
              )}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 14, color: "var(--ink-2)", fontSize: 13, lineHeight: 1.7 }}>
                <CalendarDays size={16} style={{ flexShrink: 0 }} />
                <span>Pay for any plan during trial to keep access uninterrupted. The paid subscription starts the day after trial ends and remains active for one full month from that date. For Flex Pro, the ₹999 advance is adjusted against the first monthly bill.</span>
              </div>
              <div className="sb-trial-feats">
                {["Full OPD Access", "Patient Management", "Doctor Scheduling", "Reports"].map(f => <span key={f} className="sb-trial-feat">✓ {f}</span>)}
              </div>
              <button className="sb-plan-cta sb-current" style={{ width: "auto", padding: "0 22px" }} onClick={() => document.querySelector("#sb-plans-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                Choose a plan <ArrowUpRight size={14} />
              </button>
            </div>

            <div className="sb-ring-wrap">
              <svg width="110" height="110" viewBox="0 0 110 110">
                <circle cx="55" cy="55" r="46" fill="none" stroke="var(--blue-200)" strokeWidth="9" />
                <motion.circle cx="55" cy="55" r="46" fill="none" stroke="var(--blue-700)" strokeWidth="9" strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 46} initial={{ strokeDashoffset: 2 * Math.PI * 46 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 46 * (1 - (daysLeft / 14)) }} transition={{ duration: 1.2, delay: .4, ease: "easeOut" }}
                  style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }} />
                <text x="55" y="51" textAnchor="middle" style={{ fontSize: 24, fontWeight: 800, fill: "var(--navy-800)" }}>{daysLeft}</text>
                <text x="55" y="64" textAnchor="middle" style={{ fontSize: 9, fontWeight: 700, fill: "var(--ink-4)", letterSpacing: .6 }}>DAYS LEFT</text>
              </svg>
            </div>
          </motion.div>
        )}

        {/* Pending approval */}
        {data?.mode === "PENDING_APPROVAL" && (
          <motion.div className="sb-alert-banner pending" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .1 }}>
            <span className="sb-alert-icon"><Lock size={20} aria-hidden="true" /></span>
            <div>
              <h3>Account under review</h3>
              <p>Your clinic account is currently being reviewed by our admin team. You'll receive access once the approval is complete.</p>
            </div>
          </motion.div>
        )}

        {/* Rejected */}
        {data?.mode === "REJECTED" && (
          <motion.div className="sb-alert-banner rejected" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .1 }}>
            <span className="sb-alert-icon"><XCircle size={20} aria-hidden="true" /></span>
            <div>
              <h3>Application rejected</h3>
              <p>Unfortunately, your clinic application has been rejected. Please contact support for more information or to reapply.</p>
            </div>
          </motion.div>
        )}

        {/* WhatsApp addon */}
        {data?.isPostpaid === true && (
          <section className="sb-addon-card">
            <div className="sb-addon-row">
              <div>
                <strong><MessageCircle size={17} /> WhatsApp Patient Communication</strong>
                <p>Optional patient alerts. OTP, prescription portal passwords, and consultation-completed messages are always included in every plan.</p>
                {data?.whatsappAddonEnabled ? (
                  <p style={{ color: "var(--green)", fontWeight: 700, marginTop: 6 }}>
                    This month: {data.whatsappMessageCount ?? 0} total messages · {data.whatsappPatientCount ?? 0} patient recipients · {Number(data.whatsappAverageMessagesPerPatient ?? 0).toFixed(2)} avg messages/recipient
                  </p>
                ) : (
                  <p style={{ color: "var(--ink-3)", fontWeight: 700, marginTop: 6 }}>Optional patient alerts are disabled. The three always-on patient messages remain enabled.</p>
                )}
              </div>
              <button type="button" onClick={toggleWhatsAppCommunication} disabled={whatsappToggleLoading}
                className="sb-addon-toggle" style={{ border: `1.5px solid ${whatsappAddonEnabled ? "var(--green-line)" : "var(--line-2)"}`, color: whatsappAddonEnabled ? "var(--green)" : "var(--ink-2)", cursor: whatsappToggleLoading ? "wait" : "pointer" }}>
                {whatsappToggleLoading ? "Updating…" : whatsappAddonEnabled ? "Disable" : "Enable"}
              </button>
            </div>
          </section>
        )}

        <div className="sb-section-divider" />

        {/* Expired plan fallback */}
        {data?.mode === "EXPIRED" && data?.lastPlanName && (
          <motion.div className="sb-expired-card" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .08 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 800, color: "#9a5b1a" }}>Previous plan details</div>
                <div style={{ fontFamily: "var(--font-disp)", fontSize: 28, fontWeight: 600, color: "#7c2d12", marginTop: 8 }}>{data.lastPlanName}</div>
              </div>
              <span style={{ background: "#fff", border: "1px solid #fdba74", borderRadius: 999, padding: "8px 12px", fontSize: 11, fontWeight: 800, color: "#9a5b1a" }}>{data.lastPlanType || "Prepaid"}</span>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
              <span style={{ background: "rgba(255,255,255,0.6)", border: "1px solid rgba(251,146,60,0.25)", borderRadius: 10, padding: "8px 12px", fontSize: 12, fontWeight: 700, color: "#7c2d12" }}>Last start: {data.lastStartDate ? formatDashboardDate(data.lastStartDate) : "-"}</span>
              <span style={{ background: "rgba(255,255,255,0.6)", border: "1px solid rgba(251,146,60,0.25)", borderRadius: 10, padding: "8px 12px", fontSize: 12, fontWeight: 700, color: "#7c2d12" }}>Last end: {data.lastEndDate ? formatDashboardDate(data.lastEndDate) : "-"}</span>
            </div>
            {Number(data?.pendingOverageAmount ?? 0) > 0 && (
              <div style={{ marginTop: 16, padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.7)", border: "1px solid rgba(251,146,60,0.25)", color: "#7c2d12", fontSize: 12, lineHeight: 1.6, fontWeight: 700 }}>
                {String(data.overageStatus || "PENDING").toUpperCase() === "CLEAR"
                  ? `Previous month overage of ₹${Number(data.pendingOverageAmount ?? 0).toFixed(2)} is already cleared. You can activate a new plan.`
                  : `Previous month overage: ₹${Number(data.pendingOverageAmount ?? 0).toFixed(2)} · ${String(data.overageStatus || "PENDING").toUpperCase()}. It will be settled in this plan activation.`}
              </div>
            )}
          </motion.div>
        )}

        {/* Active plan usage strip */}
        {data?.mode === "SUBSCRIBED" && (
          <motion.div className="sb-usage-strip" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .1 }}>
            <div className="sb-usage-heading">
              <div>
                <div className="sb-usage-eyebrow">{data.planName || "Current plan"}</div>
                <div className="sb-usage-planname">{data.planType || (data.isPostpaid ? "Postpaid" : "Prepaid")}</div>
              </div>
              <span className="sb-usage-status-pill" style={{
                background: data.billingStatus === "DUE" ? "#fef3c7" : data.billingStatus === "GRACE" ? "#fef9c3" : data.billingStatus === "EXPIRED" ? "#fee2e2" : "#dcfce7",
                color: data.billingStatus === "DUE" ? "#92400e" : data.billingStatus === "GRACE" ? "#854d0e" : data.billingStatus === "EXPIRED" ? "#991b1b" : "#166534",
              }}>{data.billingStatus || "ACTIVE"}</span>
            </div>

            <div className="sb-usage-billing">
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="sb-usage-price">
                  {data.isPostpaid ? `₹${Number(data.minimumMonthlyCharge ?? data.monthlyPrice ?? 0).toFixed(2)}` : `₹${Number(data.subscriptionPrice ?? data.monthlyPrice ?? 0).toFixed(2)}`}
                  <span>{data.isPostpaid ? "/ month minimum" : data.durationMonths === 12 ? "/ year" : data.durationMonths === 3 ? "/ 3 months" : "/ month"}</span>
                </div>
                <div className="sb-usage-dates">
                  <span>Start: {formatDashboardDate(data.startDate)}</span>
                  <span>{data.isPostpaid ? `Due: ${formatDashboardDate(data.invoiceDueDate || data.endDate)}` : `Expiry: ${formatDashboardDate(data.endDate)}`}</span>
                </div>
              </div>

              {!isFlexProPostpaid && (
                <div className="sb-usage-ring-wrap" style={{ position: "relative", width: 110, height: 110 }}>
                  <svg width="110" height="110" viewBox="0 0 110 110">
                    <circle cx="55" cy="55" r="42" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="9" />
                    <motion.circle cx="55" cy="55" r="42" fill="none" stroke={usagePercent > 80 ? "#fca5a5" : "#93c5fd"} strokeWidth="9" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 42} initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                      animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - usagePercent / 100) }} transition={{ duration: 1.2, delay: .3, ease: "easeOut" }}
                      style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }} />
                  </svg>
                  <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
                    <div><div style={{ fontSize: 22, fontWeight: 800 }}>{usagePercent}%</div><div style={{ fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", opacity: .7 }}>Used</div></div>
                  </div>
                </div>
              )}
            </div>

            <div className="sb-usage-stats">
              {(isFlexProPostpaid ? [
                { label: "Used visits", value: data.usedAppointments ?? 0 },
                { label: "Minimum", value: `₹${Number(data.minimumMonthlyCharge ?? data.monthlyPrice ?? 0).toFixed(2)}` },
                { label: "Pending", value: `₹${Number(data.outstandingAmount ?? 0).toFixed(2)}` },
                { label: "Status", value: data.billingStatus || "ACTIVE" },
              ] : [
                { label: annualUsageEnabled ? "Annual used visits" : "Used visits", value: data.usedAppointments ?? 0 },
                { label: annualUsageEnabled ? "Annual allowed" : "Allowed", value: data.totalAllowed ?? 0 },
                { label: annualUsageEnabled ? "Annual remaining" : "Remaining", value: remaining },
                { label: annualUsageEnabled ? "Annual overage visits" : "Overage visits", value: data.overageCount ?? 0 },
              ]).map((item) => (
                <div key={item.label} className="sb-usage-stat"><div className="k">{item.label}</div><div className="v">{item.value}</div></div>
              ))}
            </div>

            <div className="sb-usage-foot">
              <span>{data.isPostpaid ? `Pending invoice amount: ₹${Number(data.outstandingAmount ?? 0).toFixed(2)}` : `Grace window: ${data.graceEndDate ? formatDashboardDate(data.graceEndDate) : "N/A"}`}</span>
              <span style={{ fontWeight: 700 }}>Overage status: {data.overageStatus || "CLEAR"}</span>
            </div>

            {Number(data?.pendingOverageAmount ?? data?.previousOverageAmount ?? 0) > 0 && (
              <div className="sb-usage-note">Previous persisted overage debt: ₹{Number(data?.pendingOverageAmount ?? data?.previousOverageAmount ?? 0).toFixed(2)} · {String(data.overageStatus || "PENDING").toUpperCase()}. This amount is settled during the next activation.</div>
            )}
            {Number(data?.estimatedOverageAmount ?? data?.currentOverageAmount ?? 0) > 0 && (
              <div className="sb-usage-note info">Current-plan estimated overage: ₹{Number(data?.estimatedOverageAmount ?? data?.currentOverageAmount ?? 0).toFixed(2)}. This is an estimate for the current cycle, not previous debt.</div>
            )}
          </motion.div>
        )}

        <AnnualUsageTable data={data} />

        <div className="sb-section-divider" />

        {/* Postpaid bill card */}
        {showMonthlyBillCard && (
          <div className="sb-bill-card">
            <div style={{ height: 4, width: 120, borderRadius: 999, background: "linear-gradient(90deg,#fbbf24,#f97316,#ea580c)", marginBottom: 18 }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 900, color: "#9a5b1a" }}>Flex Pro billing summary</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 36, fontWeight: 900, color: "#7c2d12", lineHeight: 1.05, marginTop: 10 }}>₹{Number(data.outstandingAmount ?? 0).toFixed(2)}</div>
              </div>
              <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "9px 14px", borderRadius: 999, fontSize: 11, fontWeight: 800, color: "#7c2d12", background: "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,237,213,0.75))", border: "1px solid rgba(251,146,60,0.45)" }}>
                Due: {formatDashboardDate(data.invoiceDueDate || data.endDate)}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 20, marginBottom: 18 }}>
              <span style={{ background: "rgba(255,255,255,0.5)", border: "1px solid rgba(251,146,60,0.2)", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#7c2d12", fontWeight: 700 }}>Start: {formatDashboardDate(data.startDate)}</span>
              <span style={{ background: "rgba(255,255,255,0.5)", border: "1px solid rgba(251,146,60,0.2)", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#7c2d12", fontWeight: 700 }}>Cycle: {formatDashboardDate(data.startDate)} — {formatDashboardDate(data.invoiceDueDate || data.endDate)}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14 }}>
              <div className="sb-bill-stat"><div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c2d12", opacity: .8 }}>Used</div><div style={{ fontFamily: "var(--font-mono)", fontSize: 25, fontWeight: 800, color: "#7c2d12", marginTop: 6 }}>{data.usedAppointments ?? 0}</div></div>
              <div className="sb-bill-stat"><div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c2d12", opacity: .8 }}>Minimum</div><div style={{ fontFamily: "var(--font-mono)", fontSize: 25, fontWeight: 800, color: "#7c2d12", marginTop: 6 }}>₹{Number(data.minimumMonthlyCharge ?? 0).toFixed(2)}</div></div>
              <div className="sb-bill-stat"><div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c2d12", opacity: .8 }}>Pending</div><div style={{ fontFamily: "var(--font-mono)", fontSize: 25, fontWeight: 800, color: "#7c2d12", marginTop: 6 }}>₹{Number(data.outstandingAmount ?? 0).toFixed(2)}</div></div>
              <div className="sb-bill-stat"><div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7c2d12", opacity: .8 }}>Status</div><div style={{ fontSize: 20, fontWeight: 800, color: "#7c2d12", marginTop: 6, textTransform: "uppercase" }}>{data.billingStatus || "ACTIVE"}</div></div>
            </div>
            <div style={{ marginTop: 18, fontSize: 14, color: "#7c2d12", lineHeight: 1.6, fontWeight: 700 }}>Pending invoice amount: ₹{Number(data.outstandingAmount ?? 0).toFixed(2)}</div>
          </div>
        )}

        {/* Plan selection */}
        {data?.mode !== "PENDING_APPROVAL" && data?.mode !== "REJECTED" ? (
          <>
            <div id="sb-plans-anchor" className="sb-section-eyebrow" style={{ marginTop: 28 }}>Select plan</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
              <h3 className="sb-section-title">Choose your plan</h3>
              {isTrialMode && (
                <span style={{ background: "var(--amber-bg)", border: "1px solid var(--amber-line)", color: "#7a4b08", borderRadius: 20, padding: "4px 14px", fontSize: 11, fontWeight: 700 }}>
                  Pay during trial; every paid plan starts after the trial ends, with no interruption.
                </span>
              )}
            </div>

            <div role="group" aria-label="Billing duration" style={{ display: "flex", justifyContent: "center", width: "100%", marginBottom: 20 }}>
              <div className="sb-duration-toggle">
                {[{ months: 1, label: "Monthly" }, { months: 3, label: "Quarterly" }, { months: 12, label: "Yearly" }].map(option => (
                  <button key={option.months} type="button" onClick={() => setBillingDuration(option.months)}
                    className={`sb-duration-btn ${billingDuration === option.months ? "active" : ""}`}
                    style={{ cursor: "pointer" }}>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .18 }} style={{ marginBottom: 10 }}>
              <div className={`sb-plans-grid${billingDuration !== 1 ? " three-plans" : ""}`}>
                {[1, 2, 3, 4].filter(id => billingDuration === 1 || id !== 4).map(id => {
                  const plan = planDetails[id];
                  const backendPlanId = planIds[id];
                  const isCurrent = data?.planId === backendPlanId;
                  const price = getPlanPrice(id, billingDuration);
                  const save = getBillingSavings(id);
                  const allFeats = billingDuration === 12 && id !== 4 ? plan.yearlyFeatures : plan.features;
                  const primary = allFeats.filter(f => !isSecondaryFeature(f));
                  const secondary = allFeats.filter(f => isSecondaryFeature(f));

                  let ribbon = null;
                  if (isCurrent) ribbon = <span className="sb-plan-ribbon current"><Check size={12} /> Current plan</span>;
                  else if (plan.popular) ribbon = <span className="sb-plan-ribbon popular"><Sparkles size={12} /> Most popular</span>;
                  else if (plan.flexpro) ribbon = <span className="sb-plan-ribbon premium"><Crown size={12} /> Premium</span>;

                  return (
                    <motion.div key={id} className={`sb-plan-card ${plan.key}${isCurrent ? " sb-current" : ""}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .08 * id }}>
                      {ribbon}
                      <div className="sb-plan-head">
                        <span className="sb-plan-icon"><plan.icon size={20} strokeWidth={2} /></span>
                        <div><div className="sb-plan-name">{plan.name}</div><div className="sb-plan-desc">{plan.desc}</div></div>
                      </div>
                      <div className="sb-plan-price">₹{price}<span>{billingDuration === 12 ? "/year" : billingDuration === 3 ? "/3 months" : "/month"}</span></div>
                      {save && <div className="sb-plan-save">Save ₹{save.amount.toLocaleString("en-IN")} ({Math.round(save.percentage)}%)</div>}
                      {planStats(plan)}
                      <ul className="sb-plan-feats">{primary.map(f => <li key={f}><FeatureIcon text={f} /><span>{f}</span></li>)}</ul>
                      {secondary.length > 0 && (
                        <>
                          <div className="sb-feat-divider" />
                          <ul className="sb-plan-feats secondary">{secondary.map(f => <li key={f}><FeatureIcon text={f} /><span>{f}</span></li>)}</ul>
                        </>
                      )}
                      <button type="button" className={`sb-plan-cta ${isCurrent ? "sb-current" : ""}`} disabled={processing || backendPlanId == null} onClick={() => handlePlanClick(backendPlanId)}
                        title={isTrialMode ? "Paid subscriptions during trial start the day after trial ends and remain active for one full month from that date." : ""}>
                        {isCurrent ? "Renew plan" : <>Choose plan <ArrowRight size={14} /></>}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Invoices */}
            <div className="sb-billing-head">
              <div>
                <div className="sb-section-eyebrow" style={{ marginTop: 0 }}>Billing history</div>
                <h3 className="sb-section-title">Recent invoices</h3>
              </div>
              <button type="button" className="sb-history-btn" onClick={openInvoicesPage}>View all invoices <ArrowRight size={14} /></button>
            </div>

            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .28 }}>
              {recentInvoices.length === 0 ? (
                <div className="sb-empty"><strong>No billing records yet</strong><p>Your first invoice will appear after your first payment.</p></div>
              ) : (
                <div className="sb-inv-wrap">
                  <table className="sb-inv-table">
                    <thead><tr><th>#</th><th>Invoice</th><th>Billing cycle</th><th>Amount</th><th>Status</th></tr></thead>
                    <tbody>
                      {recentInvoices.map((inv, i) => (
                        <tr key={i}>
                          <td style={{ color: "var(--ink-4)", fontWeight: 600, width: 40 }}>{i + 1}</td>
                          <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><span className="sb-inv-icon"><Receipt size={16} /></span><span className="sb-inv-num">{inv.invoiceNumber}</span></div></td>
                          <td><div className="sb-inv-month">{inv.billingCycleLabel || inv.billingMonth || "-"}</div></td>
                          <td><div className="sb-inv-amount">₹{inv.totalAmount}</div></td>
                          <td><span className="sb-inv-paid">{inv.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </>
        ) : data?.mode === "PENDING_APPROVAL" ? (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .18 }} className="sb-alert-banner pending" style={{ display: "block", textAlign: "center" }}>
            <h3 style={{ marginBottom: 8 }}>Clinic not yet approved</h3>
            <p style={{ maxWidth: "60ch", margin: "0 auto" }}>Your clinic is awaiting admin approval. Once approved, you'll be able to select a plan and make payments.<br />Please check back shortly or contact support for assistance.</p>
          </motion.div>
        ) : data?.mode === "REJECTED" ? (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .18 }} className="sb-alert-banner rejected" style={{ display: "block", textAlign: "center" }}>
            <h3 style={{ marginBottom: 8 }}>Application rejected</h3>
            <p style={{ maxWidth: "60ch", margin: "0 auto" }}>Unfortunately, your clinic application has been rejected. Please contact our support team to discuss this decision or to reapply.</p>
          </motion.div>
        ) : null}

        {/* Upgrade confirm modal */}
        <AnimatePresence>
          {upgradeConfirm && (
            <motion.div className="sb-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setUpgradeConfirm(null)}>
              <motion.div className="sb-modal" initial={{ scale: .93, opacity: 0, y: 18 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .93, opacity: 0 }} transition={{ type: "spring", stiffness: 360, damping: 28 }} onClick={e => e.stopPropagation()}>
                <div className="sb-modal-head">
                  <div><h3 className="sb-modal-title">Confirm plan change</h3><p className="sb-modal-subtitle">{isTrialMode ? "Scheduled after your trial" : "Review the billing schedule"}</p></div>
                  <button className="sb-modal-close" aria-label="Close plan change" onClick={() => setUpgradeConfirm(null)}><X size={17} /></button>
                </div>
                <div className="sb-modal-body">
                  <div style={{ textAlign: "center", padding: "12px 0 20px" }}>
                    <div style={{ width: 52, height: 52, margin: "0 auto 12px", borderRadius: 14, display: "grid", placeItems: "center", background: "linear-gradient(135deg,#5478ee,#1749d6)", color: "#fff", boxShadow: "0 8px 18px rgba(23,73,214,.28)" }}>
                      <RefreshCw size={28} strokeWidth={2.4} aria-hidden="true" />
                    </div>
                    <p style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.7 }}>
                      You're switching to <strong style={{ color: "var(--ink)" }}>{planDetails[Object.keys(planIds).find(cardId => planIds[cardId] === upgradeConfirm?.planId)]?.name}</strong>.
                      <br />{isTrialMode ? "Your paid plan will start after the current trial ends; access continues during the trial." : "Your billing will follow the applicable current billing-period schedule."}
                    </p>
                  </div>
                </div>
                <div className="sb-modal-footer">
                  <button className="sb-primary-btn" onClick={() => { const s = upgradeConfirm; setUpgradeConfirm(null); createOrder(s.planId, s.durationMonths); }}>Confirm &amp; continue</button>
                  <button className="sb-ghost-btn" onClick={() => setUpgradeConfirm(null)}>Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Checkout modal */}
        <AnimatePresence>
          {paymentPreview && (
            <motion.div className="sb-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setPaymentPreview(null)}>
              <motion.div className="sb-modal" initial={{ scale: .93, opacity: 0, y: 22 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .93, opacity: 0 }} transition={{ type: "spring", stiffness: 360, damping: 28 }} onClick={e => e.stopPropagation()}>
                <div className="sb-modal-head">
                  <div><h3 className="sb-modal-title">Order summary</h3><p className="sb-modal-subtitle">Review before payment</p></div>
                  <button className="sb-modal-close" aria-label="Close order summary" onClick={() => setPaymentPreview(null)}><X size={17} /></button>
                </div>
                <div className="sb-modal-body">
                  <div className="sb-order-row">
                    <div className="sb-order-row-label"><Receipt size={18} aria-hidden="true" />{planDetails[Object.keys(planIds).find(cardId => planIds[cardId] === paymentPreview.planId)]?.name} Plan · {paymentPreview.durationMonths === 12 ? "Yearly" : paymentPreview.durationMonths === 3 ? "Quarterly" : "Monthly"}</div>
                    <div className="sb-order-row-price">₹{paymentPreview.basePrice}</div>
                  </div>

                  {paymentPreview.isPostpaid && (
                    <>
                      <div className="sb-order-row" style={{ background: "var(--blue-100)", borderColor: "var(--blue-200)" }}>
                        <div className="sb-order-row-label" style={{ color: "var(--blue-700)" }}><Wallet size={16} /> Flex Pro security advance</div>
                        <div className="sb-order-row-price">₹{Number(paymentPreview.postpaidAdvanceAmount ?? 0).toFixed(2)}</div>
                      </div>
                      <div style={{ margin: "-2px 0 10px", padding: "0 12px", color: "var(--blue-700)", fontSize: 11, lineHeight: 1.5 }}>One-time advance credit, adjusted against the first Flex Pro monthly bill. It is included in the total payable below and is not a recurring fee.</div>
                    </>
                  )}

                  <div className="sb-order-row" style={{ opacity: .95 }}>
                    <div className="sb-order-row-label"><Check size={16} /> E-Prescription Included</div>
                    <div className="sb-order-row-price">Included</div>
                  </div>

                  {Number(paymentPreview.overageAmount ?? paymentPreview.pendingOverageAmount ?? 0) > 0 && (
                    <>
                      <div className="sb-order-row" style={{ background: "var(--red-bg)", borderColor: "var(--red-line)" }}>
                        <div className="sb-order-row-label" style={{ color: "var(--red)" }}>Previous persisted overage debt · {paymentPreview.overageStatus || "PENDING"}</div>
                        <div className="sb-order-row-price" style={{ color: "var(--red)" }}>₹{Number(paymentPreview.overageAmount ?? paymentPreview.pendingOverageAmount ?? 0).toFixed(2)}</div>
                      </div>
                      <div style={{ margin: "8px 0 0", padding: "10px 12px", border: "1px solid var(--red-line)", background: "var(--red-bg)", color: "var(--red)", borderRadius: 8, fontSize: 12, lineHeight: 1.6 }}>This persisted overage debt is included in the total payable below.</div>
                    </>
                  )}

                  {Number(paymentPreview.estimatedOverageAmount ?? paymentPreview.currentOverageAmount ?? 0) > 0 && (
                    <div style={{ margin: "8px 0 0", padding: "10px 12px", border: "1px solid var(--blue-200)", background: "var(--blue-100)", color: "var(--navy-800)", borderRadius: 8, fontSize: 12, lineHeight: 1.6 }}>
                      Current-plan estimated overage: ₹{Number(paymentPreview.estimatedOverageAmount ?? paymentPreview.currentOverageAmount ?? 0).toFixed(2)}. This estimate is informational and is separate from previous persisted debt.
                    </div>
                  )}

                  <div className="sb-divider" />
                  <div className="sb-total-row"><div className="sb-total-label">Total payable</div><div className="sb-total-val">₹{paymentPreview.totalPayable}</div></div>
                </div>
                <div className="sb-modal-footer">
                  <button className="sb-primary-btn" disabled={processing} onClick={proceedToPayment}>
                    {processing ? <><div className="sb-btn-spinner" /> Processing…</> : "Proceed to payment →"}
                  </button>
                  <button className="sb-ghost-btn" onClick={() => setPaymentPreview(null)}>Cancel</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </>
  );
};

export default SubscriptionPage;